import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Seo } from "@/components/Seo";
import { ArticleCard } from "@/components/cards/ArticleCard";
import { Reveal } from "@/components/common/Reveal";
import { Clock, ArrowLeft } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

export default function ArticleDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    api.get(`/articles/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="max-w-3xl mx-auto px-6 py-32 text-center"><h1 className="font-display text-3xl">Article not found</h1><Link to="/knowledge" className="mt-4 inline-block text-[var(--cdt-primary)] font-semibold">Back to Knowledge Center</Link></div>;
  if (!data) return <div className="max-w-3xl mx-auto px-6 py-24 space-y-6"><Skeleton className="h-10 w-3/4" /><Skeleton className="h-72 w-full rounded-[var(--cdt-radius-lg)]" /><Skeleton className="h-40 w-full" /></div>;

  const a = data.article;
  const jsonLd = { "@context": "https://schema.org", "@type": "Article", headline: a.title, description: a.excerpt, author: { "@type": "Organization", name: a.author }, image: a.cover_image };

  return (
    <>
      <Seo title={a.title} description={a.excerpt} path={`/knowledge/${slug}`} image={a.cover_image} jsonLd={jsonLd} type="article" />
      <article className="pt-12 lg:pt-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/knowledge" className="inline-flex items-center gap-1.5 text-sm text-[var(--cdt-muted)] hover:text-[var(--cdt-primary)]"><ArrowLeft className="h-4 w-4" /> Knowledge Center</Link>
          <Reveal>
            <Badge className="mt-5 bg-[rgba(15,92,90,0.1)] text-[var(--cdt-primary)] hover:bg-[rgba(15,92,90,0.1)]">{a.category}</Badge>
            <h1 className="font-display text-3xl md:text-4xl leading-tight tracking-[-0.02em] text-[var(--cdt-text)] mt-3">{a.title}</h1>
            <div className="mt-3 flex items-center gap-3 text-sm text-[var(--cdt-muted)]"><span>{a.author}</span><span>·</span><span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {a.read_minutes} min read</span></div>
          </Reveal>
          <Reveal delay={0.08}><div className="mt-6 rounded-[var(--cdt-radius-lg)] overflow-hidden shadow-[var(--cdt-shadow-md)]"><img src={a.cover_image} alt={a.title} className="w-full h-64 lg:h-96 object-cover" /></div></Reveal>
          <div className="prose-cdt mt-8" data-testid="article-content" dangerouslySetInnerHTML={{ __html: a.content }} />
        </div>
      </article>

      {data.related?.length > 0 && (
        <section className="py-16 mt-8 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-2xl text-[var(--cdt-text)] mb-8">Related articles</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">{data.related.map((r, i) => <ArticleCard key={r.slug} article={r} index={i} />)}</div>
          </div>
        </section>
      )}
    </>
  );
}
